/**
 * Navigation store slice for routing and navigation state management
 * Manages current path, breadcrumbs, and navigation history
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { NavigationStore, Breadcrumb } from '../types'
import { devtoolsDev } from '../middleware'

/**
 * Maximum number of history entries to keep
 */
const MAX_HISTORY_ENTRIES = 50

/**
 * Initial navigation state
 */
const initialNavigationState = {
  currentPath: '/',
  breadcrumbs: [],
  history: []
}

/**
 * Creates the navigation store with actions
 */
const createNavigationStore = () => create<NavigationStore>()(
  devtoolsDev(
    // @ts-ignore
    persist(
      immer((set) => ({
        // Initial state
        ...initialNavigationState,

        // Actions
        setCurrentPath: (path: string) => {
          set((state) => {
            const previousPath = state.currentPath
            state.currentPath = path
            
            // Add to history if different from current path
            if (previousPath !== path) {
              state.history.push(path)
              
              // Limit history size
              if (state.history.length > MAX_HISTORY_ENTRIES) {
                state.history = state.history.slice(-MAX_HISTORY_ENTRIES)
              }
            }
          })
        },

        setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => {
          set((state) => {
            state.breadcrumbs = breadcrumbs
          })
        },

        addToHistory: (path: string) => {
          set((state) => {
            // Don't add if it's the same as the last entry
            const lastEntry = state.history[state.history.length - 1]
            if (lastEntry !== path) {
              state.history.push(path)
              
              // Limit history size
              if (state.history.length > MAX_HISTORY_ENTRIES) {
                state.history = state.history.slice(-MAX_HISTORY_ENTRIES)
              }
            }
          })
        },

        clearHistory: () => {
          set((state) => {
            state.history = []
          })
        }
      })),
      {
        name: 'taqat-navigation-store',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          history: state.history.slice(-10) // Only persist last 10 history entries
        })
      }
    ),
    'NavigationStore'
  )
)

/**
 * Navigation store instance
 */
export const navigationStore = createNavigationStore()

/**
 * Navigation store hook for components
 * @returns Navigation store state and actions
 */
export const useNavigationStore = () => navigationStore()

/**
 * Hook for current path and path management
 * @returns Current path and path actions
 */
export const useCurrentPath = () => {
  const { currentPath, setCurrentPath } = useNavigationStore()
  
  return {
    currentPath,
    setCurrentPath
  }
}

/**
 * Hook for breadcrumb management
 * @returns Breadcrumbs and breadcrumb actions
 */
export const useBreadcrumbs = () => {
  const { breadcrumbs, setBreadcrumbs } = useNavigationStore()
  
  return {
    breadcrumbs,
    setBreadcrumbs,
    // Convenience method to add a single breadcrumb
    addBreadcrumb: (breadcrumb: Breadcrumb) => {
      setBreadcrumbs([...breadcrumbs, breadcrumb])
    },
    // Convenience method to set breadcrumbs from path
    setBreadcrumbsFromPath: (path: string) => {
      const segments = path.split('/').filter(Boolean)
      const breadcrumbs: Breadcrumb[] = segments.map((segment, index) => ({
        label: segment.charAt(0).toUpperCase() + segment.slice(1),
        path: '/' + segments.slice(0, index + 1).join('/')
      }))
      setBreadcrumbs(breadcrumbs)
    }
  }
}

/**
 * Hook for navigation history
 * @returns Navigation history and history actions
 */
export const useNavigationHistory = () => {
  const { history, addToHistory, clearHistory } = useNavigationStore()
  
  return {
    history,
    addToHistory,
    clearHistory,
    // Convenience methods
    canGoBack: history.length > 1,
    previousPath: history.length > 1 ? history[history.length - 2] : null,
    lastPath: history.length > 0 ? history[history.length - 1] : null
  }
}

/**
 * Hook for complete navigation state
 * @returns All navigation state and actions
 */
export const useNavigation = () => {
  const { currentPath, breadcrumbs, history, setCurrentPath, setBreadcrumbs, addToHistory, clearHistory } = useNavigationStore()
  
  return {
    currentPath,
    breadcrumbs,
    history,
    setCurrentPath,
    setBreadcrumbs,
    addToHistory,
    clearHistory,
    // Convenience methods
    canGoBack: history.length > 1,
    previousPath: history.length > 1 ? history[history.length - 2] : null
  }
}

/**
 * Selector hook for current path only
 * @returns Current path
 */
export const useCurrentPathOnly = () => {
  return navigationStore((state) => state.currentPath)
}

/**
 * Selector hook for breadcrumbs only
 * @returns Current breadcrumbs
 */
export const useBreadcrumbsOnly = () => {
  return navigationStore((state) => state.breadcrumbs)
}

/**
 * Selector hook for navigation history length
 * @returns Number of entries in navigation history
 */
export const useHistoryLength = () => {
  return navigationStore((state) => state.history.length)
}

/**
 * Utility function to generate breadcrumbs from a path
 * @param path - The path to generate breadcrumbs from
 * @param customLabels - Optional custom labels for path segments
 * @returns Array of breadcrumb objects
 */
export const generateBreadcrumbs = (
  path: string, 
  customLabels?: Record<string, string>
): Breadcrumb[] => {
  const segments = path.split('/').filter(Boolean)
  
  return segments.map((segment, index) => {
    const segmentPath = '/' + segments.slice(0, index + 1).join('/')
    const label = customLabels?.[segment] || segment.charAt(0).toUpperCase() + segment.slice(1)
    
    return {
      label,
      path: segmentPath
    }
  })
}

/**
 * Utility function to check if a path is active
 * @param path - Path to check
 * @param currentPath - Current path (optional, will use store if not provided)
 * @param exact - Whether the match should be exact
 * @returns Boolean indicating if path is active
 */
export const isPathActive = (
  path: string, 
  currentPath?: string, 
  exact: boolean = false
): boolean => {
  const current = currentPath || navigationStore.getState().currentPath
  
  if (exact) {
    return current === path
  }
  
  return current.startsWith(path)
}